import { sanitizeInput } from './security';

export const validateApiKey = (apiKey: string): boolean => {
  if (!apiKey || typeof apiKey !== 'string') return false;
  const sanitized = sanitizeInput(apiKey);
  return sanitized.length >= 20 && (sanitized.startsWith('sk-') || sanitized.length >= 30);
};

export const validateUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  
  try {
    const parsedUrl = new URL(sanitizeInput(url));
    return ['http:', 'https:'].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
};

export const validatePrompt = (prompt: string): boolean => {
  if (!prompt || typeof prompt !== 'string') return false;
  const sanitized = sanitizeInput(prompt);
  return sanitized.length > 0 && sanitized.length <= 2000;
};

export const validateModel = (model: string): boolean => {
  if (!model || typeof model !== 'string') return false;
  const sanitized = sanitizeInput(model);
  return sanitized.length > 0 && sanitized.length <= 100 && /^[a-zA-Z0-9\-_.]+$/.test(sanitized);
};

export const validatePersona = (persona: string): boolean => {
  if (!persona || typeof persona !== 'string') return false;
  const sanitized = sanitizeInput(persona);
  const allowedPersonas = ['jailbreaker', 'bias-probe', 'misinformation-tester', 'toxicity-inducer', 'privacy-extractor'];
  return allowedPersonas.includes(sanitized);
};

export const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  const sanitized = sanitizeInput(email);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(sanitized) && sanitized.length <= 254 && sanitized.length >= 5;
};

export const validatePassword = (password: string): boolean => {
  if (!password || typeof password !== 'string') return false;
  const hasMinLength = password.length >= 8;
  const hasMaxLength = password.length <= 128;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const noCommonPatterns = !/(password|123456|qwerty|admin|user)/i.test(password);
  
  return hasMinLength && hasMaxLength && hasUpperCase && hasLowerCase && (hasNumbers || hasSpecialChar) && noCommonPatterns;
};

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export const validateAssessmentInputs = (
  targetUrl: string,
  basePrompt: string,
  targetModel: string,
  persona: string
): ValidationResult => {
  const errors: string[] = [];

  if (!validateUrl(targetUrl)) {
    errors.push('Please enter a valid target URL (must be HTTP or HTTPS)');
  }

  if (!validatePrompt(basePrompt)) {
    errors.push('Please enter a valid prompt (1-2000 characters)');
  }

  if (!validateModel(targetModel)) {
    errors.push('Please enter a valid model name (1-100 characters)');
  }
  
  if (!validatePersona(persona)) {
    errors.push('Please select a valid red team persona');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateAuthInputs = (email: string, password: string): ValidationResult => {
  const errors: string[] = [];

  if (!validateEmail(email)) {
    errors.push('Please enter a valid email address');
  }

  if (!validatePassword(password)) {
    errors.push('Password must be 8-128 characters with uppercase, lowercase, and numbers/special characters');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};